import { getInstructionStatusLabel } from '@data/instruction';
import { statusFLowsStrings } from '@data/statusFlow';
import { CustomsRequestStatusName, CustomsStatusName } from '@models/next/CustomsRequestStatus';
import { DeliveryDestinationOptionsType } from '@models/next/DeliveryDestinationOptionsType';
import { DeliveryRequestStatusName } from '@models/next/DeliveryRequestStatus';
import { DOStatusName } from '@models/next/DOStatus';
import { LocalFile, OptionalLocalFile } from '@models/next/FileType';
import { InstructionStatusName } from '@models/next/Instruction';
import { MoveOutStatusName } from '@models/next/MoveOutStatus';
import { PreAlertStatusName } from '@models/next/PreAlertStatus';
import { StatusData } from '@models/next/StatusData';
import { includes } from '@utils/types';
import { z } from 'zod';

export const CustomsStatus = z.enum([
  ...InstructionStatusName.options,
  ...CustomsStatusName.options,
  ...CustomsRequestStatusName.options,
  // Virtual next statuses
  '予備申入り',
  '本申告入り',
]);

export type CustomsStatus = z.TypeOf<typeof CustomsStatus>;

export const DeliveryStatus = z.enum([
  ...InstructionStatusName.options,
  ...DeliveryRequestStatusName.options,
]);

export type DeliveryStatus = z.TypeOf<typeof DeliveryStatus>;

export const InvoiceStatus = z.enum(['未作成', '作成中', '発行済', '修正中', '全部発行完了']);

export type InvoiceStatus = z.TypeOf<typeof InvoiceStatus>;

export const PaymentSlipStatus = z.enum(['未作成', '下書き', '作成済', '修正中', '全部発行完了']);

export type PaymentSlipStatus = z.TypeOf<typeof PaymentSlipStatus>;

const getStatusFlow = <T extends z.ZodEnum<any>>(StatusType: T) =>
  z.object({
    status_flows: z.array(
      StatusData.extend({
        name: StatusType,
      }),
    ),
    warning: z.boolean(),
    count: z.number().nullish(),
  });

export const StatusFlows = z.object({
  delivery_destination_type: DeliveryDestinationOptionsType.nullish(),
  pre_alert_status_flow: getStatusFlow(PreAlertStatusName),
  customs_status_flow: getStatusFlow(CustomsStatus),
  delivery_status_flow: getStatusFlow(DeliveryStatus),
  do_status_flow: getStatusFlow(DOStatusName),
  move_out_flow: getStatusFlow(MoveOutStatusName),
  invoice_status_flow: getStatusFlow(InvoiceStatus),
  payment_slip_status_flow: getStatusFlow(PaymentSlipStatus),
});

export type StatusFlows = z.TypeOf<typeof StatusFlows>;

export const DOInfoForm = z
  .object({
    do_less_no: z.string().nullish().label(statusFLowsStrings.DONumber),
    do_less_no_file: OptionalLocalFile.label(statusFLowsStrings.DOFile),
  })
  .refine((data) => data.do_less_no || data.do_less_no_file, {
    message: statusFLowsStrings.DORequired,
    path: ['do_less_no'],
  });

export const MoveOutInfoForm = z.object({
  move_out_no_file: LocalFile.label(statusFLowsStrings.moveOutFile),
});

export class StatusFlowsHelper {
  static getNextPreAlertStatus(status: PreAlertStatusName): PreAlertStatusName | '' {
    switch (status) {
      case '-':
        return '作成済';
      case '作成済':
        return 'LOCKED';
      case '修正中':
        return 'LOCKED';
      case 'LOCKED':
        return '';
    }
  }

  static getNextCustomsStatus(status: CustomsStatus): CustomsStatus | '' {
    switch (status) {
      case '未送信':
        return '送信済';
      case '送信済':
        return '確認済み';
      case '修正依頼済':
        return '確認済み';
      case '確認済み':
        return '通関依頼書作成済';
      case '通関依頼書作成済':
        return '通関依頼書送信済';
      case '通関リンク作成済':
      case '通関依頼書送信済':
        return '通関準備中';
      case '通関準備中':
        return '予備申入り';
      case '予備申入り': // Acutally virtual
      case '予備申入り 区1':
      case '予備申入り 区2':
      case '予備申入り 区3':
        return '本申告入り';
      case '本申告入り': // Acutally virtual
      case '本申告入り 区1':
      case '本申告入り 区2':
        return '輸入許可';
      case '本申告入り 区3':
        return '税関検査';
      case '税関検査':
        return '輸入許可';
      case '輸入許可':
        return '';
    }
  }

  static getNextDeliveryStatus(status: DeliveryStatus): DeliveryStatus | '' {
    switch (status) {
      case '未送信':
        return '送信済';
      case '送信済':
        return '確認済み';
      case '修正依頼済':
        return '確認済み';
      case '確認済み':
        return '配車依頼書作成済';
      case '配車依頼書作成済':
        return '';
      case 'メール送信済未読':
        return 'メール送信済既読';
      case 'メール送信済既読':
        return '配車完了';
      case 'CSV送信済':
        return '横持ち送信済';
      case '横持ち送信済':
        return '配車完了';
      case '配車リンク作成済':
        return '配車完了';
      case '配車完了':
        return '';
    }
  }

  static getNextDOStatus(status: DOStatusName): DOStatusName | '' {
    switch (status) {
      case '搬入先＆海上A/N金額待ち':
        return '';
      case '支払伝票発行待ち':
        return '支払伝票発行済';
      case '支払伝票発行済':
        return 'D/O手配済';
      case 'D/O手配済':
        return '';
      case 'D/O手配不要':
        return '';
      case '支払伝票作成待ち':
        return '支払伝票作成済';
      case '支払伝票作成済':
        return '支払伝票送信済';
      case '支払伝票送信済':
        return '支払伝票入金済';
      case '支払伝票入金済':
        return 'D/O手配済';
    }
  }

  static getNextMoveOutStatus(
    status: MoveOutStatusName,
    deliveryDestionationOptionsType: DeliveryDestinationOptionsType | null,
  ): MoveOutStatusName | '' {
    switch (status) {
      case '搬入先未確認':
        return '';
      case '搬入先が不一致':
        return '';
      case '搬入先確認済':
        if (deliveryDestionationOptionsType === 'FC A/N自社倉庫') {
          return '配車完了待ち';
        } else if (deliveryDestionationOptionsType === '他社A/N他社倉庫') {
          return '搬出手配済';
        } else {
          return '';
        }
      case '配車完了待ち':
        return '搬出可能';
      case '搬出可能':
        return '搬出手配済';
      case '搬出手配済':
        return '';
      case '搬出オーダー送信待ち':
        return '';
      case '搬出手配不要':
        return '';
    }
  }

  static getNextInvoiceStatus(status: InvoiceStatus): InvoiceStatus | '' {
    switch (status) {
      case '未作成':
      case '作成中':
      case '修正中':
        return '発行済';
      case '発行済':
      case '全部発行完了':
        return '';
    }
  }

  static getNextPaymentSlipStatus(status: PaymentSlipStatus): PaymentSlipStatus | '' {
    switch (status) {
      case '未作成':
        return '作成済';
      case '下書き':
        return '作成済';
      case '修正中':
        return '作成済';
      case '作成済':
      case '全部発行完了':
        return '';
    }
  }

  static getCustomsStatusLabel(status: CustomsStatus, isAdmin: boolean) {
    if (includes(InstructionStatusName.options, status)) {
      return getInstructionStatusLabel(status, isAdmin);
    } else {
      return status;
    }
  }

  static getDeliveryStatusLabel(status: DeliveryStatus, isAdmin: boolean) {
    if (includes(InstructionStatusName.options, status)) {
      return getInstructionStatusLabel(status, isAdmin);
    } else {
      return status;
    }
  }
}
